import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { DragEvent } from 'react';
import type { LucideIcon } from 'lucide-react';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Download,
  Eraser,
  FileArchive,
  History,
  Info,
  Loader2,
  Settings2,
  Sparkles,
  Stethoscope,
  Trash2,
  Upload,
  Wrench,
  X,
  XCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/utils/cn';
import {
  analyzePlayablePackage,
  deleteHistoryEntry,
  downloadBase64Zip,
  downloadHistoryZip,
  downloadReportJson,
  fetchFixGroups,
  fetchHistory,
  fixPlayablePackage,
  type DoctorChange,
  type DoctorFixGroup,
  type DoctorHistoryEntry,
  type DoctorIssue,
  type DoctorReport,
  type DoctorSeverity,
} from '@/api/modules/playable-doctor';

const ACCEPT = '.zip,.html,.htm';

// 操作区按钮统一样式：主操作同一种青色系（描边+淡底），禁用态统一灰化
const ACTION_BTN =
  'flex items-center gap-2 rounded-xl border border-neon-cyan/50 bg-neon-cyan/10 px-4 py-2 text-sm font-medium text-neon-cyan transition hover:bg-neon-cyan/15 disabled:cursor-not-allowed disabled:border-border/40 disabled:bg-transparent disabled:text-muted-foreground/50';
// 次操作（清空）：中性描边，hover 变红提示破坏性
const GHOST_BTN =
  'flex items-center gap-2 rounded-xl border border-border/70 bg-transparent px-4 py-2 text-sm font-medium text-muted-foreground transition hover:border-red-400/60 hover:text-red-400';

const SEVERITY_META: Record<DoctorSeverity, { label: string; icon: LucideIcon; badge: string; bar: string }> = {
  P0: {
    label: 'P0 阻断投放',
    icon: XCircle,
    badge: 'bg-red-500/15 text-red-300 border-red-500/30',
    bar: 'bg-red-500',
  },
  P1: {
    label: 'P1 功能/数据',
    icon: AlertTriangle,
    badge: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
    bar: 'bg-amber-500',
  },
  P2: {
    label: 'P2 建议优化',
    icon: Info,
    badge: 'bg-sky-500/15 text-sky-300 border-sky-500/30',
    bar: 'bg-sky-500',
  },
};

const CATEGORY_LABELS: Record<string, string> = {
  structure: '文件结构',
  sdk: 'SDK 引入',
  syntax: '语法',
  event: '事件协议',
  lifecycle: '生命周期',
  tracking: '埋点',
  media: '媒体兼容',
  logic: '逻辑',
  log: '日志',
  delivery: '交付',
};

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function scoreTone(score: number): string {
  if (score >= 85) return 'text-emerald-400';
  if (score >= 60) return 'text-amber-400';
  return 'text-red-400';
}

function IssueRow({ issue }: { issue: DoctorIssue }): JSX.Element {
  const [open, setOpen] = useState(false);
  const meta = SEVERITY_META[issue.severity];
  const Icon = meta.icon;
  return (
    <div className="rounded-xl border border-border/50 bg-[hsl(var(--card)/0.45)]">
      <button
        type="button"
        className="flex w-full items-start gap-3 px-4 py-3 text-left"
        onClick={() => setOpen((v) => !v)}
      >
        <Icon
          size={16}
          className={cn(
            'mt-0.5 shrink-0',
            issue.severity === 'P0' ? 'text-red-400' : issue.severity === 'P1' ? 'text-amber-400' : 'text-sky-400',
          )}
        />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-foreground">{issue.title}</span>
            <span className={cn('rounded-md border px-1.5 py-0.5 text-[10px]', meta.badge)}>{issue.severity}</span>
            <span className="rounded-md border border-border/40 px-1.5 py-0.5 text-[10px] text-muted-foreground">
              {CATEGORY_LABELS[issue.category] ?? issue.category}
            </span>
            <span className="rounded-md border border-border/40 px-1.5 py-0.5 text-[10px] font-mono text-muted-foreground">
              {issue.id}
            </span>
            {issue.fixed ? (
              <span className="rounded-md border border-emerald-500/30 bg-emerald-500/10 px-1.5 py-0.5 text-[10px] text-emerald-300">
                已修复
              </span>
            ) : issue.fixable ? (
              <span className="rounded-md border border-neon-cyan/30 bg-neon-cyan/10 px-1.5 py-0.5 text-[10px] text-neon-cyan">
                可自动修复
              </span>
            ) : (
              <span className="rounded-md border border-border/40 px-1.5 py-0.5 text-[10px] text-muted-foreground">
                需人工
              </span>
            )}
          </div>
          {open && (
            <div className="mt-2 space-y-2">
              <p className="text-xs leading-relaxed text-muted-foreground">{issue.detail}</p>
              {issue.file ? (
                <p className="text-[11px] text-muted-foreground/80">
                  位置：{issue.file ?? ''}
                  {issue.line ? `:${issue.line}` : ''}
                </p>
              ) : null}
              {issue.snippet && (
                <pre className="overflow-x-auto rounded-lg bg-slate-950/70 p-2 text-[11px] leading-relaxed text-slate-300">
                  {issue.snippet}
                </pre>
              )}
              {issue.fixSummary && (
                <p className="text-[11px] text-neon-cyan/90">修复方案：{issue.fixSummary}</p>
              )}
            </div>
          )}
        </div>
        <span className="mt-0.5 shrink-0 text-muted-foreground">
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </span>
      </button>
    </div>
  );
}

function ChangeRow({ change }: { change: DoctorChange }): JSX.Element {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl border border-border/50 bg-[hsl(var(--card)/0.45)]">
      <button type="button" className="flex w-full items-center gap-3 px-4 py-3 text-left" onClick={() => setOpen((v) => !v)}>
        <Sparkles size={15} className="shrink-0 text-neon-cyan" />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-foreground">{change.description}</span>
            <span className="rounded-md border border-border/40 px-1.5 py-0.5 text-[10px] font-mono text-muted-foreground">
              {change.issueId}
            </span>
          </div>
          <p className="mt-0.5 truncate text-[11px] text-muted-foreground">{change.file}</p>
        </div>
        <span className="shrink-0 text-muted-foreground">{open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</span>
      </button>
      {open && (
        <div className="space-y-2 px-4 pb-4">
          <div>
            <p className="mb-1 text-[11px] text-red-300">修改前</p>
            <pre className="max-h-56 overflow-auto rounded-lg bg-slate-950/70 p-2 text-[11px] leading-relaxed text-slate-300">
              {change.before || '（新增）'}
            </pre>
          </div>
          <div>
            <p className="mb-1 text-[11px] text-emerald-300">修改后</p>
            <pre className="max-h-56 overflow-auto rounded-lg bg-slate-950/70 p-2 text-[11px] leading-relaxed text-slate-300">
              {change.after || '（删除）'}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
}

function ReportPanel({
  report,
  title,
  fixedMode,
  onDownloadReport,
}: {
  report: DoctorReport;
  title: string;
  fixedMode: boolean;
  onDownloadReport: () => void;
}): JSX.Element {
  const [filter, setFilter] = useState<'all' | DoctorSeverity>('all');
  const issues = useMemo(
    () => (filter === 'all' ? report.issues : report.issues.filter((i) => i.severity === filter)),
    [report.issues, filter],
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground">
          <Stethoscope size={16} className="text-neon-cyan" />
          {title}
          {fixedMode && (
            <span className="rounded-md border border-emerald-500/30 bg-emerald-500/10 px-1.5 py-0.5 text-[10px] text-emerald-300">
              修复后
            </span>
          )}
        </h3>
        <button
          type="button"
          onClick={onDownloadReport}
          className="flex items-center gap-1.5 rounded-lg border border-border/60 px-3 py-1.5 text-xs text-muted-foreground transition hover:text-foreground"
        >
          <Download size={13} /> 下载 JSON 报告
        </button>
      </div>

      {/* 评分 + 概览 */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-2xl border border-border/60 bg-[hsl(var(--card)/0.55)] p-4">
          <p className="text-xs text-muted-foreground">健康评分</p>
          <p className={cn('mt-1 text-3xl font-bold tabular-nums', scoreTone(report.score))}>{report.score}</p>
          <p className="mt-1 text-[11px] text-muted-foreground/80">满分 100，P0×20 P1×6 P2×2 扣分</p>
        </div>
        {(['P0', 'P1', 'P2'] as DoctorSeverity[]).map((sev) => {
          const meta = SEVERITY_META[sev];
          const count = sev === 'P0' ? report.summary.p0 : sev === 'P1' ? report.summary.p1 : report.summary.p2;
          return (
            <div key={sev} className="rounded-2xl border border-border/60 bg-[hsl(var(--card)/0.55)] p-4">
              <p className={cn('text-xs', meta.badge.split(' ')[1])}>{meta.label}</p>
              <p className="mt-1 text-3xl font-bold tabular-nums text-foreground">{count}</p>
              <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-border/40">
                <div
                  className={cn('h-full rounded-full', meta.bar)}
                  style={{ width: `${report.summary.total ? Math.round((count / report.summary.total) * 100) : 0}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* 入口/封面/文件 */}
      <div className="flex flex-wrap gap-2 text-[11px]">
        <span className="rounded-lg border border-border/50 bg-[hsl(var(--card)/0.45)] px-2.5 py-1 text-muted-foreground">
          入口：<span className="text-foreground">{report.entryFile ?? '未找到'}</span>
        </span>
        <span className="rounded-lg border border-border/50 bg-[hsl(var(--card)/0.45)] px-2.5 py-1 text-muted-foreground">
          封面图：<span className={report.coverImage ? 'text-foreground' : 'text-amber-300'}>{report.coverImage ?? '缺失'}</span>
        </span>
        <span className="rounded-lg border border-border/50 bg-[hsl(var(--card)/0.45)] px-2.5 py-1 text-muted-foreground">
          文件数：<span className="text-foreground">{report.files.length}</span>
        </span>
        <span className="rounded-lg border border-border/50 bg-[hsl(var(--card)/0.45)] px-2.5 py-1 text-muted-foreground">
          可自动修复：<span className="text-neon-cyan">{report.summary.fixable}</span>
          {fixedMode && <> · 已修复：<span className="text-emerald-300">{report.summary.fixedCount}</span></>}
        </span>
      </div>

      {/* 严重度筛选 */}
      <div className="flex flex-wrap items-center gap-2">
        {(['all', 'P0', 'P1', 'P2'] as const).map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => setFilter(key)}
            className={cn(
              'rounded-lg border px-3 py-1 text-xs transition',
              filter === key
                ? 'border-neon-cyan/50 bg-neon-cyan/10 text-neon-cyan'
                : 'border-border/50 text-muted-foreground hover:text-foreground',
            )}
          >
            {key === 'all' ? `全部 ${report.issues.length}` : `${key} ${report.issues.filter((i) => i.severity === key).length}`}
          </button>
        ))}
      </div>

      {/* 问题列表 */}
      <div className="space-y-2">
        {issues.length === 0 ? (
          <div className="flex items-center gap-2 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-6 text-sm text-emerald-300">
            <CheckCircle2 size={16} /> 该筛选下没有问题
          </div>
        ) : (
          issues.map((issue) => <IssueRow key={`${issue.id}-${issue.title}`} issue={issue} />)
        )}
      </div>

      {/* 文件清单 */}
      <details className="rounded-xl border border-border/50 bg-[hsl(var(--card)/0.35)]">
        <summary className="cursor-pointer px-4 py-3 text-xs text-muted-foreground">包内文件清单（{report.files.length}）</summary>
        <div className="max-h-64 overflow-auto px-4 pb-4">
          <table className="w-full text-left text-[11px]">
            <thead className="text-muted-foreground/70">
              <tr>
                <th className="py-1 font-normal">路径</th>
                <th className="py-1 font-normal">大小</th>
                <th className="py-1 font-normal">角色</th>
              </tr>
            </thead>
            <tbody className="font-mono text-muted-foreground">
              {report.files.map((f) => (
                <tr key={f.path} className="border-t border-border/30">
                  <td className="max-w-[420px] truncate py-1 pr-3 text-foreground/90">{f.path}</td>
                  <td className="py-1 pr-3 whitespace-nowrap">{formatSize(f.size)}</td>
                  <td className="py-1 whitespace-nowrap">
                    {[f.isEntry && '入口', f.isSdk && 'SDK', f.isCover && '封面'].filter(Boolean).join(' / ') || '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </div>
  );
}

/** 一键修复前的分组复选框：勾选要应用的修复分组 */
function FixOptionsModal({
  groups,
  disabled,
  onToggle,
  onConfirm,
  onCancel,
  busy,
}: {
  groups: DoctorFixGroup[];
  disabled: string[];
  onToggle: (key: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
  busy: boolean;
}): JSX.Element {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onCancel}>
      <div
        className="max-h-[80vh] w-full max-w-lg overflow-auto rounded-2xl border border-border/60 bg-[hsl(var(--card))] p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-center justify-between">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground">
            <Settings2 size={16} className="text-neon-cyan" /> 选择要应用的修复
          </h3>
          <button type="button" onClick={onCancel} className="text-muted-foreground hover:text-foreground">
            <X size={16} />
          </button>
        </div>
        <p className="mb-3 text-xs text-muted-foreground">取消勾选的分组不会生效；需人工项（如封面图）不在列表中。</p>
        <div className="space-y-2">
          {groups.map((g) => {
            const checked = !disabled.includes(g.key);
            return (
              <label
                key={g.key}
                className={cn(
                  'flex cursor-pointer items-start gap-3 rounded-xl border px-3 py-2 transition',
                  checked ? 'border-neon-cyan/40 bg-neon-cyan/5' : 'border-border/40 opacity-60',
                )}
              >
                <input type="checkbox" checked={checked} onChange={() => onToggle(g.key)} className="mt-0.5 accent-cyan-400" />
                <span className="min-w-0">
                  <span className="block text-sm text-foreground">{g.label}</span>
                  <span className="block text-[11px] text-muted-foreground">{g.hint}</span>
                </span>
              </label>
            );
          })}
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-xl border border-border/60 px-4 py-2 text-sm text-muted-foreground hover:text-foreground"
          >
            取消
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={onConfirm}
            className="flex items-center gap-2 rounded-xl border border-neon-cyan/50 bg-neon-cyan/10 px-4 py-2 text-sm font-medium text-neon-cyan hover:bg-neon-cyan/15 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Wrench size={15} />}
            开始修复并下载
          </button>
        </div>
      </div>
    </div>
  );
}

/** 历史修复记录面板 */
function HistoryPanel({
  entries,
  onDownload,
  onDelete,
  loading,
}: {
  entries: DoctorHistoryEntry[];
  onDownload: (e: DoctorHistoryEntry) => void;
  onDelete: (e: DoctorHistoryEntry) => void;
  loading: boolean;
}): JSX.Element {
  return (
    <div className="rounded-2xl border border-border/50 bg-[hsl(var(--card)/0.35)] p-4">
      <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-foreground">
        <History size={16} className="text-neon-cyan" /> 历史修复记录（{entries.length}）
      </h3>
      {loading ? (
        <p className="text-xs text-muted-foreground">加载中…</p>
      ) : entries.length === 0 ? (
        <p className="text-xs text-muted-foreground">暂无记录，修复后会自动归档到这里。</p>
      ) : (
        <div className="max-h-[calc(100vh-14rem)] space-y-2 overflow-auto">
          {entries.map((e) => (
            <div key={e.id} className="flex items-center gap-3 rounded-xl border border-border/40 bg-[hsl(var(--card)/0.5)] px-3 py-2">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm text-foreground">{e.sourceName}</p>
                <p className="text-[11px] text-muted-foreground">
                  {new Date(e.createdAt).toLocaleString()} · 评分 {e.beforeScore} → {e.score} · 修复 {e.changeIds.length} 项 · {formatSize(e.sizeBytes)}
                  {e.disabled.length ? ` · 跳过 ${e.disabled.length} 组` : ''}
                </p>
              </div>
              <button
                type="button"
                onClick={() => onDownload(e)}
                className="flex shrink-0 items-center gap-1 rounded-lg border border-border/50 px-2.5 py-1 text-xs text-muted-foreground hover:text-neon-cyan"
              >
                <Download size={13} /> 下载
              </button>
              <button
                type="button"
                onClick={() => onDelete(e)}
                className="flex shrink-0 items-center gap-1 rounded-lg border border-border/50 px-2.5 py-1 text-xs text-muted-foreground hover:text-red-400"
              >
                <Trash2 size={13} /> 删除
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function PlayableDoctor(): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [dragging, setDragging] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [fixing, setFixing] = useState(false);
  const [report, setReport] = useState<DoctorReport | null>(null);
  const [fixedReport, setFixedReport] = useState<DoctorReport | null>(null);
  const [allGroups, setAllGroups] = useState<DoctorFixGroup[]>([]);
  const [history, setHistory] = useState<DoctorHistoryEntry[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [showFixModal, setShowFixModal] = useState(false);
  const [disabledKeys, setDisabledKeys] = useState<string[]>([]);

  const refreshHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      setHistory(await fetchHistory());
    } catch {
      /* 历史加载失败不阻断主流程 */
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFixGroups()
      .then(setAllGroups)
      .catch(() => undefined);
    refreshHistory();
  }, [refreshHistory]);

  const acceptFile = useCallback((f: File) => {
    if (!/\.(zip|html?)$/i.test(f.name)) {
      toast.error('仅支持 .zip / .html / .htm');
      return;
    }
    setFile(f);
    setReport(null);
    setFixedReport(null);
  }, []);

  const onDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setDragging(false);
      const f = e.dataTransfer.files?.[0];
      if (f) acceptFile(f);
    },
    [acceptFile],
  );

  const runAnalyze = useCallback(async () => {
    if (!file) {
      toast.error('请先选择试玩 zip 或 html');
      return;
    }
    setAnalyzing(true);
    try {
      const data = await analyzePlayablePackage(file);
      setReport(data);
      setFixedReport(null);
      toast.success(`诊断完成：评分 ${data.score}，问题 ${data.summary.total} 项`);
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setAnalyzing(false);
    }
  }, [file]);

  const runFix = useCallback(
    async (disabled: string[]) => {
      if (!file) {
        toast.error('请先选择试玩 zip 或 html');
        return;
      }
      setFixing(true);
      try {
        const data = await fixPlayablePackage(file, disabled);
        setReport((prev) => prev ?? data.report);
        setFixedReport(data.report);
        downloadBase64Zip(data.zipBase64, data.zipFileName);
        toast.success(`修复完成，已自动下载：${data.zipFileName}`);
        refreshHistory();
      } catch (err) {
        toast.error((err as Error).message);
      } finally {
        setFixing(false);
      }
    },
    [file, refreshHistory],
  );

  // 当前报告中「可自动修复」命中的分组，作为复选框候选
  const applicableGroups = useMemo(() => {
    if (!report) return [];
    const ids = new Set(report.issues.filter((i) => i.fixable).map((i) => i.id));
    return allGroups.filter((g) => g.ruleIds.some((r) => ids.has(r)));
  }, [report, allGroups]);

  const openFix = useCallback(() => {
    if (!file) {
      toast.error('请先选择试玩 zip 或 html');
      return;
    }
    if (applicableGroups.length === 0) {
      // 无可勾选的自动修复项，直接走结构修复
      runFix([]);
      return;
    }
    setDisabledKeys([]);
    setShowFixModal(true);
  }, [file, applicableGroups, runFix]);

  const toggleGroup = useCallback((key: string) => {
    setDisabledKeys((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  }, []);

  // 一键清空：上传的包 + 诊断/修复报告全部重置
  const clearAll = useCallback(() => {
    setFile(null);
    setReport(null);
    setFixedReport(null);
    setShowFixModal(false);
    if (inputRef.current) inputRef.current.value = '';
    toast.info('已清空上传的包与诊断结果');
  }, []);

  const activeReport = fixedReport ?? report;
  const busy = analyzing || fixing;

  return (
    <div className="mx-auto w-full max-w-7xl space-y-6 p-6">
      {/* 头部 */}
      <div className="space-y-1">
        <h1 className="flex items-center gap-2 text-xl font-bold text-foreground">
          <Stethoscope size={20} className="text-neon-cyan" /> 试玩诊断
        </h1>
        <p className="text-sm text-muted-foreground">
          上传任意来源（豆包 / 其他 AI / 手写）生成的试玩 zip 或 html，自动检测结构、SDK、事件协议、方法定义、埋点、iOS 兼容与逻辑缺陷，并一键修复为标准可投放包。
        </p>
      </div>

      <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
        <div className="min-w-0 space-y-6">

      {/* 上传区 */}
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        className={cn(
          'flex cursor-pointer flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed px-6 py-12 transition',
          dragging
            ? 'border-neon-cyan/60 bg-neon-cyan/5'
            : 'border-border/60 bg-[hsl(var(--card)/0.35)] hover:border-neon-cyan/40',
        )}
      >
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) acceptFile(f);
            e.target.value = '';
          }}
        />
        {file ? (
          <>
            <FileArchive size={36} className="text-neon-cyan" />
            <p className="text-sm font-medium text-foreground">{file.name}</p>
            <p className="text-xs text-muted-foreground">{formatSize(file.size)} · 点击可重新选择</p>
          </>
        ) : (
          <>
            <Upload size={36} className="text-muted-foreground" />
            <p className="text-sm text-foreground">拖拽试玩 zip / html 到这里，或点击选择</p>
            <p className="text-xs text-muted-foreground">支持 .zip / .html / .htm，单文件最大 100MB</p>
          </>
        )}
      </div>

      {/* 操作区 */}
      <div className="flex flex-wrap items-center gap-3">
        <button type="button" disabled={!file || busy} onClick={runAnalyze} className={ACTION_BTN}>
          {analyzing ? <Loader2 size={15} className="animate-spin" /> : <Stethoscope size={15} />}
          开始诊断
        </button>
        {report && (
          <button type="button" disabled={!file || busy} onClick={openFix} className={ACTION_BTN}>
            {fixing ? <Loader2 size={15} className="animate-spin" /> : <Wrench size={15} />}
            一键修复并下载
          </button>
        )}
        {(file || report || fixedReport) && (
          <button type="button" disabled={busy} onClick={clearAll} className={cn(GHOST_BTN, busy && 'cursor-not-allowed opacity-50')}>
            <Eraser size={15} />
            清空
          </button>
        )}
        {fixedReport && (
          <span className="flex items-center gap-1.5 text-xs text-emerald-300">
            <CheckCircle2 size={14} /> 已生成修复包
          </span>
        )}
      </div>

      {/* 报告区 */}
      {activeReport && (
        <div className="space-y-6">
          {fixedReport && (
            <div className="rounded-2xl border border-border/50 bg-[hsl(var(--card)/0.35)] p-4">
              <p className="text-xs text-muted-foreground">
                修复前评分 <span className={scoreTone(fixedReport.beforeScore ?? report?.score ?? 0)}>{fixedReport.beforeScore ?? report?.score ?? 0}</span> → 修复后评分{' '}
                <span className={scoreTone(fixedReport.score)}>{fixedReport.score}</span>，已修复{' '}
                <span className="text-emerald-300">{fixedReport.summary.fixedCount}</span> 项。
                剩余需人工处理：P0 {fixedReport.summary.p0} · P1 {fixedReport.summary.p1} · P2 {fixedReport.summary.p2}
              </p>
            </div>
          )}

          <ReportPanel
            report={activeReport}
            title={fixedReport ? '修复后诊断报告' : '诊断报告'}
            fixedMode={!!fixedReport}
            onDownloadReport={() => downloadReportJson(activeReport)}
          />

          {activeReport.changes.length > 0 && (
            <div className="space-y-3">
              <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground">
                <Wrench size={16} className="text-neon-cyan" /> 修改方案（{activeReport.changes.length}）
              </h3>
              <div className="space-y-2">
                {activeReport.changes.map((change, idx) => (
                  <ChangeRow key={`${change.issueId}-${idx}`} change={change} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

        </div>

        {/* 历史记录（右侧栏） */}
        <aside className="min-w-0 lg:sticky lg:top-6">
          <HistoryPanel
            entries={history}
            loading={historyLoading}
            onDownload={(e) => {
              downloadHistoryZip(e.id, e.zipName).catch((err) => toast.error((err as Error).message));
            }}
            onDelete={async (e) => {
              try {
                await deleteHistoryEntry(e.id);
                toast.success('已删除历史记录');
                refreshHistory();
              } catch (err) {
                toast.error((err as Error).message);
              }
            }}
          />
        </aside>
      </div>

      {/* 修复选项弹窗 */}
      {showFixModal && (
        <FixOptionsModal
          groups={applicableGroups}
          disabled={disabledKeys}
          onToggle={toggleGroup}
          onCancel={() => setShowFixModal(false)}
          busy={fixing}
          onConfirm={() => {
            setShowFixModal(false);
            runFix(disabledKeys);
          }}
        />
      )}
    </div>
  );
}